import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Phone, Mail, MessageCircle, CalendarDays, Video, HeartHandshake } from "lucide-react";
import useLenis from "@/hooks/useLenis";
import Navbar from "@/components/Navbar";
import RotatingTagline from "@/components/RotatingTagline";
import { HOME } from "@/constants/testIds";
import useProfile, { absolutePhotoUrl } from "@/hooks/useProfile";
import { useLang } from "@/i18n/LangContext";
import { TAGLINES_EN, TAGLINES_HI, MARQUEE_TAGLINES_EN, MARQUEE_TAGLINES_HI } from "@/constants/taglines";

const ROOM_IMG =
  "https://images.unsplash.com/photo-1762544968153-b9b47435fefd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODh8MHwxfHNlYXJjaHw0fHx3YXJtJTIwbGl2aW5nJTIwcm9vbSUyMGNvbnZlcnNhdGlvbnxlbnwwfHx8fDE3ODQzODM1NzZ8MA&ixlib=rb-4.1.0&q=85";

function MaskedLine({ children, delay = 0 }) {
  return (
    <span className="masked-line">
      <motion.span
        initial={{ y: "110%" }}
        animate={{ y: "0%" }}
        transition={{ duration: 1.05, delay, ease: [0.7, 0, 0.2, 1] }}
        style={{ display: "block" }}
      >
        {children}
      </motion.span>
    </span>
  );
}

function ScrollReveal({ children, delay = 0, y = 40, className = "" }) {
  return (
    <motion.div
      initial={{ opacity: 0, y }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.9, delay, ease: [0.7, 0, 0.2, 1] }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

export default function HomePage() {
  useLenis();
  const profile = useProfile();
  const { t, lang } = useLang();
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.06]);

  const heroLines = [t("hero.l1"), t("hero.l2"), t("hero.l3"), t("hero.l4")];
  const taglines = lang === "hi" ? TAGLINES_HI : TAGLINES_EN;
  const marqueeTaglines = lang === "hi" ? MARQUEE_TAGLINES_HI : MARQUEE_TAGLINES_EN;

  const HOW_STEPS = [
    { icon: MessageCircle, title: t("howitworks.s1.title"), body: t("howitworks.s1.body") },
    { icon: CalendarDays, title: t("howitworks.s2.title"), body: t("howitworks.s2.body") },
    { icon: Video, title: t("howitworks.s3.title"), body: t("howitworks.s3.body") },
    { icon: HeartHandshake, title: t("howitworks.s4.title"), body: t("howitworks.s4.body") },
  ];
  const SERVICES = [
    { tag: "I", title: t("services.s1.title"), body: t("services.s1.body"), span: "md:col-span-7 md:row-span-2", img: ROOM_IMG },
    { tag: "II", title: t("services.s2.title"), body: t("services.s2.body"), span: "md:col-span-5" },
    { tag: "III", title: t("services.s3.title"), body: t("services.s3.body"), span: "md:col-span-5" },
  ];

  const heroPhoto =
    absolutePhotoUrl(profile?.photo_url) ||
    "https://images.unsplash.com/photo-1540566472852-b82269db3063?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHwzfHxlbGRlcmx5JTIwaW5kaWFuJTIwbWFuJTIwcG9ydHJhaXR8ZW58MHx8fHwxNzg0MzgzNTc3fDA&ixlib=rb-4.1.0&q=85";
  const introName = profile?.name || "Pritam Prakash Aggarwal";

  return (
    <div style={{ background: "var(--cream)" }} data-testid="home-page">
      <Navbar />

      {/* ===== HERO ===== */}
      <section ref={heroRef} className="relative min-h-[100vh] pt-32 pb-24 px-6 md:px-12 lg:px-20">
        <div className="max-w-[1600px] mx-auto grid grid-cols-12 gap-6 md:gap-10">
          <div className="col-span-12 lg:col-span-7 flex flex-col justify-center">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1.2, delay: 0.15 }}
              className="mono-eyebrow mb-8 flex items-center gap-3"
            >
              <span style={{ display: "inline-block", width: 32, height: 1, background: "var(--terra)" }} />
              {t("hero.eyebrow")}
            </motion.div>

            <h1 className="serif text-[13vw] md:text-[8.6vw] lg:text-[7.2vw] leading-[0.92] font-medium" style={{ letterSpacing: "-0.03em" }}>
              {heroLines.map((l, i) => (
                <MaskedLine key={i} delay={0.35 + i * 0.14}>
                  {i === 1 || i === 3 ? <em style={{ fontStyle: "italic", color: "var(--terra)" }}>{l}</em> : l}
                </MaskedLine>
              ))}
            </h1>

            {/* Rotating tagline, right below hero title */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 1 }}
              className="mt-8 flex items-baseline gap-3 flex-wrap"
              style={{ minHeight: 40 }}
            >
              <span className="mono-eyebrow flex-shrink-0" style={{ color: "var(--mute)" }}>
                {t("hero.tagline.prefix")}
              </span>
              <span
                className="serif italic text-2xl md:text-3xl lg:text-4xl"
                style={{ color: "var(--ink)", letterSpacing: "-0.015em", lineHeight: 1.1 }}
              >
                <RotatingTagline taglines={taglines} intervalMs={7000} testId="hero-rotating-tagline" />
              </span>
            </motion.div>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 1.2 }}
              className="mt-8 max-w-[520px] text-[15px] md:text-base leading-relaxed"
              style={{ color: "var(--ink-2)" }}
              dangerouslySetInnerHTML={{
                __html: t("hero.intro", { NAME: `<strong style="color:var(--ink)">${introName}</strong>` }),
              }}
            />

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 1.4 }}
              className="mt-10 flex flex-wrap items-center gap-4"
            >
              <Link to="/book" data-testid={HOME.heroBook} className="btn-terra">
                {t("hero.book")} <ArrowRight size={16} strokeWidth={1.5} />
              </Link>
              <a href="#howitworks" data-testid={HOME.heroSecondary} className="btn-ghost">
                {t("hero.read")}
              </a>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1.5, delay: 1.6 }}
              className="mt-16 grid grid-cols-3 gap-6 max-w-[520px]"
            >
              {[["40+", t("hero.stat1")], ["1 : 1", t("hero.stat2")], ["3", t("hero.stat3")]].map(([n, l], i) => (
                <div key={i} className="border-t hairline pt-4">
                  <div className="serif text-3xl md:text-4xl" style={{ letterSpacing: "-0.02em" }}>{n}</div>
                  <div className="mono-eyebrow mt-2 !text-[10px]" style={{ color: "var(--mute)" }}>{l}</div>
                </div>
              ))}
            </motion.div>
          </div>

          <div className="col-span-12 lg:col-span-5 relative">
            <motion.div
              initial={{ clipPath: "inset(100% 0 0 0)" }}
              animate={{ clipPath: "inset(0% 0 0 0)" }}
              transition={{ duration: 1.6, delay: 0.5, ease: [0.7, 0, 0.2, 1] }}
              className="relative w-full aspect-[3/4] overflow-hidden"
              style={{ border: "1px solid var(--line)", background: "var(--cream-2)" }}
            >
              <motion.img
                src={heroPhoto}
                alt="Mr. Pritam Prakash Aggarwal — portrait"
                className="editorial-img w-full h-full object-cover"
                style={{ y: imgY, scale: imgScale }}
                data-testid="hero-photo"
              />
              <div
                aria-hidden
                style={{
                  position: "absolute", inset: 0,
                  background: "radial-gradient(circle at 55% 35%, rgba(0,0,0,0) 0%, rgba(0,0,0,0) 45%, rgba(26,26,26,0.30) 100%)",
                  pointerEvents: "none",
                }}
              />
              <div style={{ position: "absolute", left: 20, bottom: 20, color: "#F4F1EA", maxWidth: 300 }}>
                <div className="serif text-base md:text-lg italic" style={{ lineHeight: 1.35 }}>
                  {t("hero.caption")}
                </div>
              </div>
            </motion.div>
          </div>
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 2, duration: 1 }}
          className="absolute bottom-8 left-6 md:left-12 flex items-center gap-3 mono-eyebrow"
        >
          <span style={{ display: "inline-block", width: 42, height: 1, background: "var(--ink)" }} />
          {t("hero.scroll")}
        </motion.div>
      </section>

      {/* ===== EDITORIAL MARQUEE with taglines ===== */}
      <section className="py-14 border-y hairline overflow-hidden" style={{ background: "var(--cream-2)" }} aria-hidden>
        <div className="marquee">
          <div className="marquee-track">
            {[...marqueeTaglines, ...marqueeTaglines, ...marqueeTaglines].map((w, i) => (
              <span
                key={i}
                className="serif italic"
                style={{
                  fontSize: "clamp(40px, 5.6vw, 88px)", letterSpacing: "-0.02em",
                  color: w === "•" ? "var(--terra)" : "var(--ink)", lineHeight: 1,
                }}
              >
                {w}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* ===== HOW IT WORKS (replaces Manifesto) ===== */}
      <section id="howitworks" className="py-32 md:py-44 px-6 md:px-12 lg:px-20 relative">
        <div className="max-w-[1600px] mx-auto">
          <ScrollReveal>
            <div className="grid grid-cols-12 gap-6 mb-20">
              <div className="col-span-12 md:col-span-3 mono-eyebrow pt-2">{t("howitworks.eyebrow")}</div>
              <div className="col-span-12 md:col-span-9">
                <h2
                  className="serif text-4xl md:text-6xl lg:text-7xl leading-[0.98] font-medium"
                  style={{ letterSpacing: "-0.02em" }}
                >
                  {t("howitworks.title.a")}
                  <em style={{ color: "var(--terra)", fontStyle: "italic" }}>{t("howitworks.title.b")}</em>
                </h2>
                <div className="mono-eyebrow mt-6" style={{ color: "var(--mute)" }}>
                  {t("howitworks.badge")}
                </div>
              </div>
            </div>
          </ScrollReveal>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
            {HOW_STEPS.map((s, i) => {
              const Icon = s.icon;
              return (
                <ScrollReveal key={i} delay={i * 0.1}>
                  <div
                    className="relative h-full p-8 border hairline flex flex-col justify-between min-h-[300px]"
                    style={{ background: "#FFFFFF" }}
                    data-testid={`how-step-${i + 1}`}
                  >
                    <div className="flex items-start justify-between">
                      <div
                        className="serif text-6xl md:text-7xl"
                        style={{ color: "var(--terra)", letterSpacing: "-0.03em", lineHeight: 1 }}
                      >
                        {String(i + 1).padStart(2, "0")}
                      </div>
                      <Icon size={22} strokeWidth={1.2} style={{ color: "var(--mute)" }} />
                    </div>
                    <div className="mt-10">
                      <h3
                        className="serif text-xl md:text-2xl"
                        style={{ letterSpacing: "-0.015em", lineHeight: 1.15 }}
                      >
                        {s.title}
                      </h3>
                      <p
                        className="mt-3 text-sm md:text-[15px] leading-relaxed"
                        style={{ color: "var(--ink-2)" }}
                      >
                        {s.body}
                      </p>
                    </div>
                  </div>
                </ScrollReveal>
              );
            })}
          </div>

          {/* Second rotating tagline as a pull quote */}
          <ScrollReveal delay={0.3}>
            <div className="mt-24 md:mt-32 border-t hairline pt-14">
              <div
                className="serif italic text-4xl md:text-6xl lg:text-7xl text-center max-w-[1100px] mx-auto"
                style={{ letterSpacing: "-0.025em", lineHeight: 1.05, color: "var(--ink)" }}
              >
                <span style={{ color: "var(--terra)", fontStyle: "italic" }}>&ldquo;&nbsp;</span>
                <RotatingTagline taglines={taglines} intervalMs={8000} testId="pullquote-rotating-tagline" />
                <span style={{ color: "var(--terra)", fontStyle: "italic" }}>&nbsp;&rdquo;</span>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* ===== HOW I CAN HELP (renamed from Practice) ===== */}
      <section id="services" className="py-32 md:py-40 px-6 md:px-12 lg:px-20" style={{ background: "var(--cream-2)" }}>
        <div className="max-w-[1600px] mx-auto">
          <ScrollReveal>
            <div className="grid grid-cols-12 gap-6 mb-20">
              <div className="col-span-12 md:col-span-3 mono-eyebrow pt-2">{t("services.eyebrow")}</div>
              <div className="col-span-12 md:col-span-9">
                <h2 className="serif text-4xl md:text-6xl lg:text-7xl leading-[0.98] font-medium" style={{ letterSpacing: "-0.02em" }}>
                  {t("services.title.a")}
                  <em style={{ fontStyle: "italic", color: "var(--olive)" }}>{t("services.title.b")}</em>
                </h2>
              </div>
            </div>
          </ScrollReveal>

          <div className="grid grid-cols-12 gap-4 md:gap-6 auto-rows-[minmax(220px,auto)]">
            {SERVICES.map((s, i) => (
              <ScrollReveal key={s.tag} delay={i * 0.08} className={`col-span-12 ${s.span}`}>
                <div className="group h-full p-8 md:p-10 relative overflow-hidden border hairline" style={{ background: "#FFFFFF" }}>
                  {s.img && (
                    <div className="absolute inset-0">
                      <img src={s.img} alt="" className="editorial-img w-full h-full object-cover" style={{ opacity: 0.14 }} />
                    </div>
                  )}
                  <div className="relative flex flex-col justify-between h-full min-h-[220px]">
                    <div className="flex items-start justify-between">
                      <div className="serif text-5xl md:text-6xl" style={{ color: "var(--terra)", fontStyle: "italic", letterSpacing: "-0.02em" }}>
                        {s.tag}
                      </div>
                      <ArrowUpRight size={20} strokeWidth={1.2} className="opacity-40 group-hover:opacity-100 transition-opacity" />
                    </div>
                    <div className="mt-10 max-w-[560px]">
                      <h3 className="serif text-3xl md:text-4xl" style={{ letterSpacing: "-0.02em" }}>{s.title}</h3>
                      <p className="mt-4 text-base leading-relaxed" style={{ color: "var(--ink-2)" }}>{s.body}</p>
                    </div>
                  </div>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* ===== CTA ===== */}
      <section id="booking" className="py-32 md:py-48 px-6 md:px-12 lg:px-20">
        <div className="max-w-[1600px] mx-auto grid grid-cols-12 gap-6">
          <ScrollReveal className="col-span-12 md:col-span-3">
            <div className="mono-eyebrow">{t("cta.eyebrow")}</div>
          </ScrollReveal>
          <div className="col-span-12 md:col-span-9">
            <ScrollReveal>
              <h2 className="serif text-5xl md:text-7xl lg:text-[104px] leading-[0.94] font-medium" style={{ letterSpacing: "-0.03em" }}>
                {t("cta.title.a")}
                <em style={{ fontStyle: "italic", color: "var(--terra)" }}>{t("cta.title.b")}</em>
              </h2>
            </ScrollReveal>
            <ScrollReveal delay={0.15}>
              <p className="mt-10 max-w-[640px] text-lg leading-relaxed" style={{ color: "var(--ink-2)" }}>{t("cta.body")}</p>
            </ScrollReveal>
            <ScrollReveal delay={0.3}>
              <div className="mt-12 flex flex-wrap items-center gap-5">
                <Link to="/book" data-testid="cta-book-btn" className="btn-terra">
                  {t("cta.begin")} <ArrowRight size={16} strokeWidth={1.5} />
                </Link>
                {profile?.email && (
                  <a href={`mailto:${profile.email}`} className="link-underline text-sm" data-testid="cta-email-link">
                    {t("cta.write")}
                  </a>
                )}
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* ===== FOOTER with contact ===== */}
      <footer className="border-t hairline py-16 px-6 md:px-12 lg:px-20" style={{ background: "var(--ink)", color: "var(--cream)" }}>
        <div className="max-w-[1600px] mx-auto grid grid-cols-12 gap-8 items-start">
          <div className="col-span-12 md:col-span-6">
            <div className="serif text-3xl md:text-5xl" style={{ letterSpacing: "-0.02em" }}>
              {profile?.name || "Pritam Prakash Aggarwal"}
            </div>
            <div className="serif italic mt-4 text-lg md:text-xl" style={{ color: "#EAE5D9", opacity: 0.85 }}>
              <RotatingTagline taglines={taglines} intervalMs={9000} testId="footer-rotating-tagline" />
            </div>
          </div>
          <div className="col-span-6 md:col-span-3">
            <div className="mono-eyebrow" style={{ color: "#a3a3a3" }}>{t("footer.contact")}</div>
            {profile?.phone && (
              <a
                href={`tel:${profile.phone.replace(/\s+/g, "")}`}
                data-testid="footer-phone"
                className="serif text-lg md:text-xl mt-3 flex items-center gap-2 link-underline"
              >
                <Phone size={14} strokeWidth={1.2} /> {profile.phone}
              </a>
            )}
            {profile?.email && (
              <a
                href={`mailto:${profile.email}`}
                data-testid="footer-email"
                className="serif text-lg md:text-xl mt-2 flex items-center gap-2 link-underline"
                style={{ wordBreak: "break-word" }}
              >
                <Mail size={14} strokeWidth={1.2} /> {profile.email}
              </a>
            )}
          </div>
          <div className="col-span-6 md:col-span-3 flex flex-col justify-between h-full">
            <div className="mono-eyebrow" style={{ color: "#a3a3a3" }}>{t("footer.rights")}</div>
            <Link to="/admin/login" className="link-underline text-xs mt-4" style={{ color: "#a3a3a3" }} data-testid="footer-admin-link">
              {t("footer.signin")}
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
